import { format } from 'date-fns';

/**
 * Pure helpers for the day-first booking agenda.
 *
 * Status semantics intentionally mirror SlotCalendarView's
 * getSlotAvailabilityStatus so the agenda never disagrees with the booking
 * forms about whether a slot is free.
 */

export const toDateKey = (date) => {
  // API now returns DATE columns as plain 'YYYY-MM-DD' strings.
  if (typeof date === 'string') return date.substring(0, 10);
  // Date objects come from the calendar (local midnight) — use local date parts.
  const d = date instanceof Date ? date : new Date(date);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const normTime = (t) => (t || '').trim().substring(0, 5);

// Extract start/end HH:MM from a timeSlot label.
// Handles "Morning Slot (06:00-12:00)", "06:00-12:00", "06:00:00-12:00:00".
const extractTimesFromLabel = (label) => {
  if (!label) return null;
  const parenMatch = label.match(/\((\d{1,2}:\d{2})(?::\d{2})?[-–](\d{1,2}:\d{2})(?::\d{2})?\)/);
  if (parenMatch) return { start: parenMatch[1].substring(0, 5), end: parenMatch[2].substring(0, 5) };
  const rangeMatch = label.match(/^(\d{1,2}:\d{2})(?::\d{2})?[-–](\d{1,2}:\d{2})(?::\d{2})?$/);
  if (rangeMatch) return { start: rangeMatch[1].substring(0, 5), end: rangeMatch[2].substring(0, 5) };
  return null;
};

export function bookingMatchesSlot(booking, slot, dateKey) {
  if (!booking || booking.status === 'cancelled') return false;

  let bookingDate = '';
  if (booking.date) {
    // Always take the first 10 chars of the string, or use UTC date parts to
    // avoid a timezone shift when the API returns "2026-06-26T00:00:00.000Z".
    bookingDate = toDateKey(booking.date);
  }
  if (bookingDate !== dateKey) return false;

  // eslint-disable-next-line eqeqeq
  if (booking.slotId != null && booking.slotId == slot.id) return true;

  const slotStart = normTime(slot.startTime);
  const slotEnd = normTime(slot.endTime);
  if (!slotStart) return false;

  if (booking.slotStartTime && booking.slotEndTime) {
    if (normTime(booking.slotStartTime) === slotStart && normTime(booking.slotEndTime) === slotEnd) {
      return true;
    }
  }

  if (booking.timeSlot) {
    const extracted = extractTimesFromLabel(booking.timeSlot);
    if (extracted && extracted.start === slotStart && extracted.end === slotEnd) return true;
    // Multi-hour bookings span several slots — occupy every slot whose start
    // falls inside the booking's [start, end) range so the extended hours show
    // as booked and can't be double-booked.
    if (extracted) {
      const toM = (t) => { const [h, m] = t.split(':').map(Number); return h * 60 + (m || 0); };
      const bStart = toM(extracted.start);
      const bEnd = toM(extracted.end);
      const sStart = toM(slotStart);
      if (bEnd - bStart > 0 && sStart >= bStart && sStart < bEnd) return true;
    }
  }

  return false;
}

export function getSlotStatus(service, slotBookings) {
  // Cricket ground: two-sides (team) occupancy.
  if (service?.type === 'cricket_match') {
    const hasBothTeamsBooking = slotBookings.some((booking) => {
      const bookingType = (booking.bookingType || booking.booking_type || '').toLowerCase();
      if (bookingType === 'both' || bookingType === 'both-teams') return true;
      const teamDetails = booking.teamDetails || {};
      return Boolean(teamDetails.teamA?.name && teamDetails.teamB?.name);
    });
    if (slotBookings.length === 0) return 'available';
    if (slotBookings.length === 1 && !hasBothTeamsBooking) return 'half-booked';
    return 'booked';
  }

  // Capacity-based services (guest lists).
  if (service?.rules?.maxGuests) {
    const maxCapacity = service.rules.maxGuests;
    const totalGuests = slotBookings.reduce((sum, b) => sum + (b.guestList?.length || 0), 0);
    if (totalGuests === 0) return 'available';
    if (totalGuests < maxCapacity) return 'half-booked';
    return 'booked';
  }

  // Individual / shareable services: a 'half' booking fills half the slot, so
  // one half → half-booked, two halves (or any full booking) → fully booked.
  const occupancy = slotBookings.reduce((sum, b) => {
    const t = (b.bookingType || b.booking_type || '').toLowerCase();
    return sum + (t === 'half' ? 0.5 : 1);
  }, 0);
  if (occupancy <= 0) return 'available';
  if (occupancy < 1) return 'half-booked';
  return 'booked';
}

/** Paid/due rollup across the bookings occupying one slot. */
export function getPaymentSummary(slotBookings) {
  const toAmount = (v) => {
    const n = parseFloat(v);
    return Number.isNaN(n) ? 0 : n;
  };
  return slotBookings.reduce(
    (acc, b) => {
      const total = toAmount(b.totalAmount);
      const paid = toAmount(b.advanceAmount);
      const due = b.balanceAmount != null ? toAmount(b.balanceAmount) : Math.max(0, total - paid);
      return { paid: acc.paid + paid, due: acc.due + due };
    },
    { paid: 0, due: 0 }
  );
}

/** Display name for the people occupying a slot. */
export function getCustomerLabel(slotBookings) {
  const names = slotBookings
    .map((b) => b.teamDetails?.teamA?.name || b.teamDetails?.teamB?.name || b.contactName || '')
    .filter(Boolean);
  if (names.length === 0) return 'Booked';
  if (names.length === 1) return names[0];
  return `${names[0]} +${names.length - 1}`;
}

const matchesGround = (serviceConfig, groundId) => {
  if (!groundId) return true;
  return Number(serviceConfig?.service?.ground_id) === Number(groundId);
};

/**
 * Build the agenda rows for one day across all (ground-filtered) services.
 * Returns rows sorted by start time:
 *   { key, slot, serviceConfig, service, status, bookings, payment }
 */
export function buildDayRows({ date, serviceConfigs = [], bookings = [], groundId = null }) {
  const dateKey = toDateKey(date);
  const rows = [];

  serviceConfigs.filter((cfg) => matchesGround(cfg, groundId)).forEach((cfg) => {
    const service = cfg.service || {};
    const slots = (service.slots && service.slots.length > 0 ? service.slots : cfg.slots) || [];

    slots.forEach((slot) => {
      const slotBookings = bookings.filter((b) => {
        if (!bookingMatchesSlot(b, slot, dateKey)) return false;
        // When a booking carries a serviceId, keep it on its own service row.
        if (b.serviceId && service.id && Number(b.serviceId) !== Number(service.id)) return false;
        return true;
      });

      rows.push({
        key: `${cfg.slotConfigurationId || service.id || 's'}-${slot.id}`,
        slot,
        serviceConfig: cfg,
        service,
        status: getSlotStatus(service, slotBookings),
        bookings: slotBookings,
        payment: getPaymentSummary(slotBookings),
      });
    });
  });

  return rows.sort((a, b) => normTime(a.slot.startTime).localeCompare(normTime(b.slot.startTime)));
}

/**
 * Day-load classification for the date-strip dots.
 *   'none'    -> no slots configured for the day (gray)
 *   'free'    -> < half of slots occupied (green)
 *   'partial' -> at least half occupied (amber)
 *   'full'    -> every slot booked (red)
 */
export function getDayLoad({ date, serviceConfigs = [], bookings = [], groundId = null }) {
  const rows = buildDayRows({ date, serviceConfigs, bookings, groundId });
  if (rows.length === 0) return 'none';

  const occupied = rows.filter((r) => r.status !== 'available').length;
  if (occupied === 0) return 'free';
  if (rows.every((r) => r.status === 'booked')) return 'full';
  return occupied / rows.length < 0.5 ? 'free' : 'partial';
}

/** Apply the All / Booked / Free / Due status chips. */
export function filterRowsByStatus(rows, statusFilter) {
  switch (statusFilter) {
    case 'booked':
      return rows.filter((r) => r.status !== 'available');
    case 'free':
      return rows.filter((r) => r.status !== 'booked');
    case 'due':
      return rows.filter((r) => r.bookings.length > 0 && r.payment.due > 0);
    default:
      return rows;
  }
}
